import type { PAGE_TYPE } from '@amzn/journeys-common';
import { useEffect } from 'react';
import { generateRefTag } from '../utils/refTagsUtils';

/**
 * Parameters for useAutoGeneratedRefTag hook
 * - isAutoGenEnabled - Whether auto-generation is currently enabled
 * - creativeName - The creative name to generate the ref tag from
 * - setRefTag - Callback to update the ref tag value
 * - pageType - Optional page type to determine prefix behavior
 */
type UseAutoGeneratedRefTagParams = {
  isAutoGenEnabled: boolean;
  creativeName: string;
  setRefTag: (tag: string) => void;
  pageType?: PAGE_TYPE;
};

/**
 * Custom hook to handle automatic ref tag generation based on creative name
 *
 * Automatically generates and sets refTag when:
 * - Auto-generation is enabled
 * - Creative name is provided
 *
 * Clears refTag when:
 * - Auto-generation is enabled but creative name is empty
 *
 * @param params - Configuration object for the hook
 */
export const useAutoGeneratedRefTag = (params: UseAutoGeneratedRefTagParams): void => {
  const { isAutoGenEnabled, creativeName, setRefTag, pageType } = params;

  useEffect(() => {
    if (isAutoGenEnabled) {
      const generatedTag = creativeName ? generateRefTag(creativeName, pageType) : '';
      setRefTag(generatedTag);
    }
  }, [
    isAutoGenEnabled,
    creativeName,
    setRefTag,
    pageType,
  ]);
};
